import { SeededRandom } from "../core/SeededRandom";
import { carveRoom } from "./RoomCarver";
import { CorridorGenerator, type RoomEntrance } from "./CorridorGenerator";
import { GridMap } from "./GridMap";
import { LevelValidator } from "./LevelValidator";
import { createMissionGraph } from "./MissionGraph";
import { RoomGenerator } from "./RoomGenerator";
import { SpawnPlanner } from "./SpawnPlanner";
import { placeDungeonProps } from "./PropPlanner";
import {
  GRID_SIZE,
  TileType,
  type DoorSpawn,
  type LevelData,
  type MissionEdge,
  type Point,
  type Room,
} from "./types";

const MAX_GENERATION_ATTEMPTS = 8;

export class LevelGenerator {
  private readonly validator = new LevelValidator();

  public generate(seed = "INFERNAL-BREACH-01"): LevelData {
    const failures: string[] = [];
    for (let attempt = 0; attempt < MAX_GENERATION_ATTEMPTS; attempt += 1) {
      const generationSeed = attempt === 0 ? seed : `${seed}:retry-${attempt}`;
      try {
        const level = this.createCandidate(seed, generationSeed, attempt);
        const validation = this.validator.validate(level);
        if (validation.valid) {
          return level;
        }
        failures.push(`${generationSeed}: ${validation.errors.join(", ")}`);
      } catch (error) {
        failures.push(
          `${generationSeed}: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    }
    throw new Error(
      `Unable to generate a valid level after ${MAX_GENERATION_ATTEMPTS} attempts: ${failures.join(" | ")}`,
    );
  }

  private createCandidate(seed: string, generationSeed: string, attempt: number): LevelData {
    const random = new SeededRandom(generationSeed);
    const graph = createMissionGraph(random.fork("graph"));
    const grid = new GridMap(GRID_SIZE, GRID_SIZE);
    const rooms = new RoomGenerator(random.fork("rooms")).generate(graph);

    for (const room of rooms) {
      carveRoom(grid, room, random.fork(`shape:${room.id}`));
    }
    this.addToxicChannels(grid, this.roomById(rooms, "toxic"), random.fork("toxic"));

    const corridorGenerator = new CorridorGenerator(grid, random.fork("corridors"), rooms);
    const corridors: Point[][] = [];
    const doors: DoorSpawn[] = [];
    for (const edge of graph.edges) {
      const targetRoom = this.roomById(rooms, edge.to);
      const corridor = corridorGenerator.connect(this.roomById(rooms, edge.from), targetRoom);
      corridors.push([...corridor.cells]);
      const door = this.createDoor(edge, corridor.entrance);
      if (door) {
        doors.push(door);
      }
    }
    for (const door of doors) {
      for (const cell of door.cells) {
        grid.set(cell, door.secret ? TileType.SecretDoor : TileType.Door);
      }
    }

    const spawns = new SpawnPlanner(random.fork("spawns"), grid);
    const startRoom = this.roomById(rooms, "start");
    const finaleRoom = this.roomById(rooms, "finale");
    const enemies = spawns.createEnemies(rooms);
    const level: LevelData = {
      mode: "procedural",
      seed,
      generationSeed,
      attempt,
      grid,
      graph,
      rooms,
      corridors,
      doors,
      enemies,
      pickups: spawns.createPickups(rooms),
      playerStart: { x: startRoom.center.x, y: startRoom.center.y + 1 },
      exit: finaleRoom.center,
      secretRoomId: "secret",
    };
    return { ...level, props: placeDungeonProps(level, random.fork("props")) };
  }

  private roomById(rooms: readonly Room[], id: Room["id"]): Room {
    const room = rooms.find((candidate) => candidate.id === id);
    if (!room) {
      throw new Error(`Missing generated room ${id}`);
    }
    return room;
  }

  private addToxicChannels(grid: GridMap, room: Room, random: SeededRandom): void {
    const vertical = random.chance(0.5);
    const offset = random.pick([-3, 3]);
    for (let y = room.y + 1; y < room.y + room.height - 1; y += 1) {
      for (let x = room.x + 1; x < room.x + room.width - 1; x += 1) {
        const distance = vertical ? x - room.center.x : y - room.center.y;
        if (
          (distance === offset || distance === offset + 1) &&
          grid.get({ x, y }) === TileType.Floor
        ) {
          grid.set({ x, y }, TileType.Toxic);
        }
      }
    }
  }

  private createDoor(edge: MissionEdge, entrance: RoomEntrance): DoorSpawn | undefined {
    if (edge.kind !== "secret" && !["tech", "dark", "finale", "supply"].includes(edge.to)) return;
    const { cell, orientation } = entrance;
    const cells = [
      cell,
      orientation === "x" ? { x: cell.x + 1, y: cell.y } : { x: cell.x, y: cell.y + 1 },
    ];
    return {
      id: `${edge.from}-${edge.to}`,
      cell,
      cells,
      orientation,
      position: { x: (cells[0]!.x + cells[1]!.x) / 2, y: (cells[0]!.y + cells[1]!.y) / 2 },
      secret: edge.kind === "secret",
      autoClose: edge.kind !== "secret" && edge.to !== "finale",
    };
  }
}
